import { useNavigate, Link } from 'react-router-dom';
import { BellIcon, MenuIcon, LogOutIcon, UserIcon, ShieldIcon, ClipboardListIcon, BookOpenIcon } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { useAuthStore } from '../../stores/authStore';
import { useBrandingStore } from '../../stores/brandingStore';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { ROUTES } from '../../constants/routes';

interface TopNavProps {
  onMobileMenuOpen: () => void;
}

export default function TopNav({ onMobileMenuOpen }: TopNavProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const user = useAuthStore((s) => s.user);
  const company = useAuthStore((s) => s.company);
  const role = useAuthStore((s) => s.role);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  const roleLabel: Record<string, string> = {
    'ic-admin': 'IC Admin',
    'ic-underwriter': 'Underwriter',
    'ic-agent': 'Agent',
    'super-admin': 'Super Admin',
    'admin': 'Admin',
  };
  const logoUrl = useBrandingStore((s) => s.config?.logo_url);

  const unreadCount = useNotificationStore((s) => s.unread_count);
  const setDrawerOpen = useNotificationStore((s) => s.setDrawerOpen);

  const initials = user
    ? `${user.first_name[0] ?? ''}${user.last_name[0] ?? ''}`.toUpperCase()
    : '?';

  function handleSignOut() {
    clearAuth();
    useBrandingStore.getState().reset();
    useSubscriptionStore.getState().reset();
    queryClient.clear();
    navigate(ROUTES.LOGIN, { replace: true });
  }

  return (
    <header className="flex h-16 shrink-0 items-center justify-between border-b bg-white px-4">
      {/* Left: mobile hamburger + logo + company name */}
      <div className="flex items-center gap-3">
        {/* Hamburger — mobile only */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={onMobileMenuOpen}
          aria-label="Open menu"
        >
          <MenuIcon className="h-5 w-5" />
        </Button>

        {/* Logo — desktop */}
        {logoUrl ? (
          <img src={logoUrl} alt="Logo" className="hidden md:block h-7 w-auto object-contain" />
        ) : (
          <span className="hidden md:block text-sm font-bold text-gray-900">Fast Policy</span>
        )}

        {company?.name && (
          <span className="hidden md:block text-sm text-gray-500">{company.name}</span>
        )}

        {/* Logo — mobile (links to dashboard) */}
        <Link to={ROUTES.DASHBOARD} className="flex md:hidden items-center gap-2 hover:opacity-80 transition-opacity">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="h-6 w-auto object-contain" />
          ) : (
            <span className="text-sm font-bold text-gray-900">Fast Policy</span>
          )}
        </Link>
      </div>

      {/* Right: help + notification bell + avatar dropdown */}
      <div className="flex items-center gap-2">
        {/* User Guide */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(ROUTES.GUIDE)}
          aria-label="User guide"
        >
          <BookOpenIcon className="h-5 w-5" />
        </Button>

        {/* Notification bell */}
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open notifications"
        >
          <BellIcon className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full p-0 text-[10px] bg-red-500 text-white border-0"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>

        {/* Avatar dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0">
              <Avatar className="h-8 w-8">
                {user?.avatar_url && <AvatarImage src={user.avatar_url} alt={user.full_name} />}
                <AvatarFallback className="text-xs bg-primary text-white">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-64">
            <div className="flex items-center gap-3 px-3 py-3">
              <Avatar className="h-10 w-10 shrink-0">
                {user?.avatar_url && <AvatarImage src={user.avatar_url} alt={user.full_name} />}
                <AvatarFallback className="text-sm bg-primary text-white">{initials}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{user?.full_name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                  {role && (
                    <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                      {roleLabel[role] ?? role}
                    </span>
                  )}
                  {company?.name && (
                    <span className="text-[10px] text-gray-400 truncate">{company.name}</span>
                  )}
                </div>
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate(ROUTES.SETTINGS_PROFILE)}>
              <UserIcon className="mr-2 h-4 w-4" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate(ROUTES.SETTINGS_MFA)}>
              <ShieldIcon className="mr-2 h-4 w-4" />
              Security & MFA
            </DropdownMenuItem>
            {(role === 'ic-admin' || role === 'super-admin' || role === 'admin') && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(ROUTES.SETTINGS_AUDIT_LOG)}>
                  <ClipboardListIcon className="mr-2 h-4 w-4" />
                  Audit Log
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-red-600 focus:text-red-600"
              onClick={handleSignOut}
            >
              <LogOutIcon className="mr-2 h-4 w-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
